import React from 'react';
import { Building2, FolderOpen, Loader2, Shield, Users } from 'lucide-react';
import { useGetOrganizationsQuery, useGetPermissionsQuery } from '../../services/profileApi';

const ProfileOrganizations: React.FC = () => {
  const {
    data: organizations,
    isLoading: isLoadingOrgs,
    error: orgsError,
  } = useGetOrganizationsQuery();
  const {
    data: permissions,
    isLoading: isLoadingPerms,
    error: permsError,
  } = useGetPermissionsQuery();

  const isLoading = isLoadingOrgs || isLoadingPerms;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (orgsError || permsError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load organization data. Please try again.
        </div>
      </div>
    );
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
      case 'owner':
        return 'bg-purple-100 text-purple-700';
      case 'project_manager':
      case 'manager':
        return 'bg-blue-100 text-blue-700';
      case 'member':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatRoleName = (role: string) => {
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="p-5 max-w-4xl">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Organizations & Projects</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          View the projects and organizations you belong to
        </p>
      </div>

      {/* Global Role */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Your Role</h2>
            <p className="text-xs text-gray-500">Your global system role</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <span
            className={`px-3 py-1.5 text-sm font-medium rounded-full ${
              permissions?.isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {permissions?.isAdmin ? 'Administrator' : 'User'}
          </span>
          <span className="text-sm text-gray-500">
            {permissions?.isAdmin
              ? 'You have full access to all system features.'
              : 'You have standard user access to the system.'}
          </span>
        </div>
      </div>

      {/* Projects List */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
              <Building2 className="w-4 h-4 text-brand-primary" />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-900">Projects</h2>
              <p className="text-xs text-gray-500">
                {organizations?.length || 0} project{organizations?.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        {organizations && organizations.length > 0 ? (
          <div className="space-y-4">
            {organizations.map(org => (
              <div
                key={org.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white rounded-lg border border-gray-200 flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{org.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Joined{' '}
                      {new Date(org.joinedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>

                <span
                  className={`px-3 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(org.role)}`}
                >
                  {formatRoleName(org.role)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-1">No Projects Yet</h3>
            <p className="text-sm text-gray-500">You haven't been added to any projects yet.</p>
          </div>
        )}
      </div>

      {/* Project Roles Breakdown */}
      {permissions?.projectRoles && permissions.projectRoles.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
              <Users className="w-4 h-4 text-brand-primary" />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-900">Project Permissions</h2>
              <p className="text-xs text-gray-500">Your role and permissions in each project</p>
            </div>
          </div>

          <div className="space-y-4">
            {permissions.projectRoles.map(projectRole => (
              <div key={projectRole.projectId} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900">{projectRole.projectName}</h3>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(projectRole.role)}`}
                  >
                    {formatRoleName(projectRole.role)}
                  </span>
                </div>

                {projectRole.permissions && projectRole.permissions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {projectRole.permissions.map((permission, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileOrganizations;
